import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MaskLine, Reveal, FadeIn, Overline } from "@/components/motion";
import { WA_URL } from "@/lib/site";
import { lenisRef } from "@/lib/lenis";

const SELECTOR = [
  { label: "Formicolii, dolori o guizzi muscolari", target: "emg" },
  { label: "Momenti di vuoto, scosse o confusione", target: "eeg" },
  { label: "Stanchezza al risveglio o russamento", target: "poligrafia" },
  { label: "Insonnia o notti agitate", target: "polisonnografia" },
];

const EXAM_BLOCKS = [
  {
    id: "emg",
    num: "01",
    name: "Elettromiografia (EMG)",
    hook: "Formicolii, dolori o guizzi che non passano? Misuriamoli.",
    referto: "Immediato",
    refertoNote: "in mano a fine esame",
    semplice: [
      "L'elettromiografia studia i nervi che dalla colonna arrivano a braccia e gambe, e i muscoli che comandano. Aiuta a capire se c'è un problema e dove si trova.",
    ],
    riconosci: [
      "Mani che formicolano o si addormentano, soprattutto di notte",
      "Bruciori o intorpidimento a mani o piedi",
      "Dolore dal collo o dalla schiena che scende lungo un braccio o una gamba",
      "Guizzi muscolari che vanno avanti da settimane",
      "Forza che manca in un braccio o in una gamba, anche dopo un trauma",
    ],
    segnale:
      "Il formicolio notturno alla mano viene spesso rimandato, nella speranza che passi da solo. È tipico del tunnel carpale. Aspettare troppo, a volte, può ridurre l'efficacia di un intervento semplice.",
    svolge: [
      "Si svolge in Studio, in Via Avigliana 38, a soli 10 min a piedi dalla stazione di Porta Susa. Piccoli stimoli elettrici misurano la conduzione dei nervi, viene poi registrata l'attività dei muscoli e a fine esame ti viene rilasciato il referto. Non serve impegnativa del medico di base.",
    ],
    domande: [
      {
        q: "«E se fosse qualcosa di grave?»",
        a: "Molti arrivano qui dopo notti passate a cercare i sintomi online. L'EMG sostituisce l'ipotesi peggiore con un dato. Qualunque cosa mostri, te la spieghiamo con calma.",
      },
    ],
    review: {
      text: "Dottore molto professionale, delicato durante l'esame e ottimo studio. Ha saputo calmarmi durante l'EMG per la mia paura degli aghi. Molto gentile",
      meta: "G.C. · Elettromiografia · settembre 2026",
    },
    chiusura: "Al posto delle ipotesi, un dato. Al posto delle ricerche notturne, una risposta.",
    cta: "Richiedi la tua elettromiografia →",
    micro: "Ti proponiamo noi un orario entro 24 ore. Se non è l'esame giusto per te, te lo diciamo prima.",
  },
  {
    id: "eeg",
    num: "02",
    name: "Elettroencefalogramma (EEG)",
    hook: "Un momento di vuoto. Poi tutto torna normale. Cos'era?",
    referto: "24–48 ore",
    refertoNote: "dall'esecuzione dell'esame",
    semplice: [
      "L'elettroencefalogramma registra l'attività elettrica del cervello. Serve a capire se alcuni episodi nascono da un'alterazione di questa attività.",
    ],
    riconosci: [
      'Momenti in cui ci si "incanta" e si perde il contatto con ciò che accade',
      "Scosse improvvise a braccia o gambe",
      "Perdite di coscienza senza una causa chiara",
      "Un genitore anziano con episodi di confusione o disorientamento",
    ],
    segnale:
      "Nei bambini e nei ragazzi le «assenze» vengono spesso liquidate come distrazione. Oppure come il carattere di un bambino un po' lento.",
    svolge: [
      "Si svolge in studio. Piccoli elettrodi appoggiati sulla testa registrano l'attività del cervello, mentre sei disteso e rilassato. Prima dell'esame ti diciamo come prepararti. Non serve l'impegnativa del medico di base.",
    ],
    domande: [
      {
        q: "«Avrei dovuto accorgermene prima?»",
        a: "Se sei un genitore, è normale chiederselo. Questi episodi si confondono facilmente con la distrazione. Accorgersene ora è già il primo passo.",
      },
      {
        q: "«Vuol dire epilessia?»",
        a: "Non necessariamente. L'EEG serve proprio a capire cosa c'è dietro un episodio, senza saltare a conclusioni.",
      },
      {
        q: "«Sto cercando per qualcun altro.»",
        a: "Puoi scriverci tu, per tuo figlio o per un genitore anziano. Ci pensiamo insieme.",
      },
    ],
    review: {
      text: "Molto attento alle sintomatologie descritte, spiegazioni esaurienti",
      meta: "T. · Visita neurologica · novembre 2025",
    },
    chiusura: "Un episodio non è un tratto del carattere. Merita di essere guardato da vicino.",
    cta: "Richiedi il tuo elettroencefalogramma →",
    micro: "Ti proponiamo noi un orario entro 24 ore. Se non è l'esame giusto, te lo diciamo prima.",
  },
  {
    id: "poligrafia",
    num: "03",
    name: "Poligrafia del sonno",
    hook: "E se la stanchezza nascesse mentre dormi?",
    referto: "7 giorni",
    refertoNote: "con indicazioni cliniche",
    semplice: [
      "La poligrafia registra respiro, ossigeno nel sangue e battito del cuore durante la notte. Mostra se il sonno viene interrotto senza che tu te ne accorga.",
      "Quando le vie respiratorie si restringono, il respiro può fermarsi per pochi secondi, anche molte volte per notte. Ogni pausa abbassa l'ossigeno e risveglia il cervello per un attimo. Al mattino non ricordi nulla: resta solo la stanchezza.",
    ],
    riconosci: [
      "Otto ore di sonno, e al mattino ti senti già senza energie",
      "Russamento forte, o pause nel respiro notate da chi ti dorme accanto",
      "Sonnolenza o colpi di sonno durante il giorno",
      "Memoria e concentrazione che non rispondono come prima",
      "Mal di testa al risveglio",
    ],
    segnale:
      "Nelle donne le apnee notturne possono dare segnali diversi: meno russamento, più stanchezza, irritabilità, mal di testa al mattino. Per questo vengono spesso attribuite allo stress. Anche russamento e qualche chilo in più vengono considerati normali, e raramente si approfondiscono.",
    svolge: [
      "Si fa a casa tua, nel tuo letto. Ritiri il dispositivo in studio e ti mostriamo come applicarlo. Si indossa in pochi minuti, prima di dormire. Il mattino dopo lo riporti, e in 7 giorni ricevi il referto. Non serve l'impegnativa del medico di base.",
    ],
    domande: [
      {
        q: "«E se fosse solo stanchezza?»",
        a: "Può darsi. Ma si può verificare in una notte, invece di sopportarla per anni.",
      },
      {
        q: "«Dovrò dormire in ospedale?»",
        a: "No. Dormi a casa tua, come ogni notte. Nessun ricovero.",
      },
      {
        q: "«Russa il mio partner, non io.»",
        a: "Puoi scriverci tu, per lui o per lei. Spesso chi si accorge per primo è chi dorme accanto.",
      },
    ],
    review: {
      text: "Molto esaustivo e competente a valutare un quadro completo dedicando tutto il tempo necessario al paziente. Trasmette tranquillità e mette a proprio agio.",
      meta: "S.F. · Prima visita neurologica presso Synapses · luglio 2026",
    },
    chiusura: "Quello che succede mentre dormi si può misurare. Non devi più sopportarlo senza sapere.",
    cta: "Richiedi la tua poligrafia del sonno →",
    micro: "Ti proponiamo noi un orario entro 24 ore. Se non è l'esame giusto, te lo diciamo prima.",
  },
  {
    id: "polisonnografia",
    num: "04",
    name: "Polisonnografia",
    hook: "Quello che fai mentre dormi, tu non puoi vederlo.",
    referto: "14 giorni",
    refertoNote: "con indicazioni cliniche",
    semplice: [
      "La polisonnografia è lo studio completo del sonno. Registra insieme attività del cervello, respiro, battito e movimenti, e ricostruisce le fasi della tua notte.",
      "Chi ha un disturbo del sonno spesso non lo sa: quando accade, sta dormendo. Il giorno dopo resta solo la stanchezza, senza una spiegazione.",
    ],
    riconosci: [
      "Insonnia che dura da mesi, nonostante tutto quello che hai provato",
      "Movimenti, parole o urla durante la notte, che al mattino non ricordi",
      "Episodi di sonnambulismo",
      "Cadute improvvise durante il giorno, quando gli altri esami non hanno trovato nulla",
    ],
    segnale:
      "I disturbi del sonno vengono spesso considerati parte del carattere: «ho sempre dormito così». Così ci si informa solo quando la qualità della vita ne risente davvero.",
    svolge: [
      "Si fa a casa tua, in una sola notte. Applichiamo i sensori e ti spieghiamo tutto, poi dormi nel tuo letto come ogni notte. In 14 giorni ricevi il referto con le indicazioni cliniche.",
      "Se serve, si abbina al test delle latenze multiple, che misura quanto facilmente ti addormenti di giorno. Non serve l'impegnativa del medico di base.",
    ],
    domande: [
      {
        q: "«Riuscirò a dormire con i sensori?»",
        a: "Se lo chiedono in molti. Te ne parliamo prima, e ti spieghiamo come funziona la notte dell'esame.",
      },
      {
        q: "«È il mio partner a notare qualcosa.»",
        a: "Spesso sono le urla, i movimenti o le parole notturne a spaventare chi dorme accanto. Puoi scriverci tu, anche per l'altra persona.",
      },
    ],
    review: {
      text: "Dottore empatico, calmo, gentile e disponibile ad ascoltare le problematiche senza metter fretta ai pazienti. Molto chiaro nel prescrivere le cure e nelle spiegazioni.",
      meta: "F.A. · Prima visita neurologica presso Synapses · aprile 2026",
    },
    chiusura: "«Ho sempre dormito così» non è una spiegazione. È un'ipotesi mai verificata.",
    cta: "Richiedi la tua polisonnografia →",
    micro: "Ti proponiamo noi un orario entro 24 ore. Se non è l'esame giusto, te lo diciamo prima.",
  },
];

const ALTRE = [
  {
    name: "Visita neurologica specialistica",
    text: "Racconti la tua storia dall'inizio, senza fretta: la visita dura il tempo giusto per ascoltarti davvero. Seguiamo mal di testa, epilessia, disturbi dei nervi e del sonno.",
    link: "Richiedi una visita →",
  },
  {
    name: "Test delle latenze multiple",
    text: "Misura quanto facilmente ti addormenti durante il giorno. Si abbina spesso alla polisonnografia, quando gli altri esami non hanno dato una risposta.",
    link: "Chiedi se fa per te →",
  },
  {
    name: "Ecografia del nervo",
    text: "Permette di vedere direttamente la forma dei nervi di braccia e gambe. È una tecnica di frontiera, che sta iniziando a diffondersi.",
    link: "Chiedi se fa per te →",
  },
];

const PASSI = [
  { num: "01", title: "Ci scrivi", text: "Raccontaci il sintomo, anche in due righe, dal modulo o su WhatsApp. Non serve l'impegnativa." },
  { num: "02", title: "Ti rispondiamo noi", text: "Entro 24 ore ti diciamo se l'esame fa per te e ti proponiamo un orario. Senza impegno." },
  { num: "03", title: "Fai l'esame e ricevi il referto", text: "Nei tempi indicati per ogni esame. Se dopo hai dubbi, puoi tornare da noi." },
];

const PRE_PNOTA = [
  {
    q: "Come faccio a sapere se è l'esame giusto?",
    a: "Non devi saperlo tu. Scrivici i sintomi: ti diciamo con sincerità cosa ha senso fare, anche se la risposta è «nessun esame».",
  },
  {
    q: "Quanto costa?",
    a: "Le tariffe sono in linea con quelle dei centri privati di Torino. Te le comunichiamo prima di fissare, così decidi con calma.",
  },
  {
    q: "Serve l'impegnativa del medico di base?",
    a: "No. Si tratta di prestazioni private, per cui l'impegnativa non è richiesta.",
  },
  {
    q: "E se l'esame non trova niente?",
    a: "Anche un risultato nella norma è un'informazione. Spesso permette di escludere alcune ipotesi e di smettere di cercare da soli. E ne parliamo insieme.",
  },
  {
    q: "E se trova qualcosa?",
    a: "Te lo spieghiamo con calma: cosa significa, quali scenari sono possibili, quali passi seguire. Non ti lasciamo solo con un referto.",
  },
  {
    q: "Dove siete?",
    a: "In Via Avigliana 38, a Torino. Quattro traverse da Porta Susa: dieci minuti a piedi, tre o quattro in auto.",
  },
];

function scrollToExam(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -96 });
  else el.scrollIntoView({ block: "start" });
}

function PageHero() {
  return (
    <section className="bg-indaco-deep text-crema pt-40 pb-24 lg:pt-52 lg:pb-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -right-40 -top-20 w-[460px] h-[460px] rounded-full border border-crema/10" />
        <div className="absolute -right-16 top-20 w-[460px] h-[460px] rounded-full border border-arancio/20" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <FadeIn>
          <Overline light>Esami e visite · Torino</Overline>
        </FadeIn>
        <h1 className="mt-8 font-sora font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="prestazioni-title">
          <MaskLine delay={0.15}>Parti da quello che senti.</MaskLine>
          <MaskLine delay={0.3}>
            <span className="text-arancio">Al resto ci pensiamo noi.</span>
          </MaskLine>
        </h1>
        <FadeIn delay={0.6}>
          <p className="mt-8 max-w-2xl text-crema/70 text-lg leading-relaxed">
            Scegli il segnale che ti somiglia di più: ti portiamo all'esame che lo studia.
            Se hai dubbi, ne parliamo prima di fissare qualsiasi cosa.
          </p>
        </FadeIn>
        <FadeIn delay={0.8}>
          <div className="mt-12 max-w-3xl" data-testid="selettore">
            <p className="text-sm uppercase tracking-[0.22em] text-crema/60 mb-5">Cosa ti preoccupa di più?</p>
            <div className="flex flex-wrap gap-3">
              {SELECTOR.map((s) => (
                <button
                  key={s.target}
                  onClick={() => scrollToExam(s.target)}
                  className="rounded-full border border-crema/30 px-6 py-3 text-[15px] md:text-base font-medium text-crema transition-colors duration-300 hover:border-arancio hover:text-arancio"
                  data-testid={`selettore-${s.target}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-6 text-[15px] md:text-base text-crema/60">
              Non ti ritrovi in nessuno?{" "}
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-crema font-semibold underline underline-offset-4"
                data-testid="selettore-whatsapp"
              >
                Scrivici
              </a>
              : ti aiutiamo a capire da dove partire.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function ExamBlock({ exam, index }) {
  const dark = index % 2 === 1;
  return (
    <section
      id={exam.id}
      className={`${dark ? "bg-crema-warm" : "bg-crema"} py-24 lg:py-32 scroll-mt-24`}
      data-testid={`exam-section-${exam.id}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="lg:sticky lg:top-32">
              <span className="font-sora text-[15px] md:text-base font-semibold tracking-widest text-arancio">{exam.num}</span>
              <h2 className="mt-4 font-sora font-bold tracking-tighter text-2xl md:text-4xl text-ink leading-tight break-words">
                {exam.name}
              </h2>
              <p className="mt-4 font-sora font-medium text-lg text-ink/80 leading-snug">{exam.hook}</p>
              <div className="mt-8 inline-flex flex-col gap-1 rounded-lg border border-indaco/25 bg-crema px-5 py-4">
                <span className="text-sm uppercase tracking-[0.2em] text-viole">Tempo di referto</span>
                <span className="font-sora font-bold text-2xl text-indaco" data-testid={`referto-${exam.id}`}>
                  {exam.referto}
                </span>
                <span className="text-sm text-viole">{exam.refertoNote}</span>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-8 space-y-12">
          <Reveal delay={0.1}>
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-viole mb-4">In parole semplici</p>
              <div className="space-y-4 max-w-2xl">
                {exam.semplice.map((p, i) => (
                  <p key={i} className="text-lg text-ink/85 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-viole mb-4">Ti riconosci?</p>
              <ul className="space-y-3 max-w-2xl">
                {exam.riconosci.map((r, i) => (
                  <li key={i} className="flex gap-3 text-[17px] md:text-lg text-ink/80 leading-relaxed">
                    <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-indaco shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="border-l-2 border-arancio pl-6 py-1">
              <p className="text-sm uppercase tracking-[0.22em] text-arancio mb-3">
                Un segnale spesso sottovalutato
              </p>
              <p className="text-[17px] md:text-lg text-ink/80 leading-relaxed max-w-2xl italic">{exam.segnale}</p>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-viole mb-4">Come si svolge</p>
              <div className="space-y-4 max-w-2xl">
                {exam.svolge.map((p, i) => (
                  <p key={i} className="text-[17px] md:text-lg text-ink/80 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.32}>
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-viole mb-5">Le domande che ti fai</p>
              <div className="space-y-6 max-w-2xl">
                {exam.domande.map((d, i) => (
                  <div key={i}>
                    <p className="font-sora font-semibold text-ink">{d.q}</p>
                    <p className="mt-2 text-[15px] md:text-base text-viole leading-relaxed">{d.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.36}>
            <blockquote
              className={`rounded-lg border p-7 min-w-0 break-words ${
                dark ? "bg-crema border-ink/5" : "bg-crema-warm border-ink/5"
              }`}
              data-testid={`review-${exam.id}`}
            >
              <p className="text-ink/85 leading-relaxed text-[17px] md:text-lg">«{exam.review.text}»</p>
              <footer className="mt-5 text-sm text-viole">{exam.review.meta}</footer>
            </blockquote>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="font-sora font-bold tracking-tight text-xl md:text-2xl text-indaco max-w-2xl leading-snug">
              {exam.chiusura}
            </p>
          </Reveal>
          <Reveal delay={0.44}>
            <div>
              <div className="flex flex-wrap items-center gap-5">
                <Link
                  to="/prenota"
                  className="group inline-flex items-center gap-3 rounded-full bg-indaco px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:bg-indaco-deep"
                  data-testid={`exam-cta-${exam.id}`}
                >
                  {exam.cta}
                </Link>
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base"
                  data-testid={`exam-wa-${exam.id}`}
                >
                  Hai un dubbio? Chiedi su WhatsApp →
                </a>
              </div>
              <p className="mt-4 text-[15px] md:text-base text-viole max-w-xl">{exam.micro}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AltrePrestazioni() {
  return (
    <section className="bg-indaco-deep text-crema py-24 lg:py-32" data-testid="altre-prestazioni">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline light>E inoltre</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-sora font-bold tracking-tighter text-3xl md:text-5xl">
            Visite e approfondimenti, prima e dopo un esame.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {ALTRE.map((p, i) => (
            <Reveal key={p.name} delay={0.1 * i}>
              <div className="rounded-lg border border-crema/15 p-8 h-full flex flex-col transition-colors duration-300 hover:border-arancio/50" data-testid={`altra-card-${i}`}>
                <h3 className="font-sora font-bold tracking-tight text-xl">{p.name}</h3>
                <p className="mt-4 text-crema/65 text-[15px] md:text-base leading-relaxed flex-1">{p.text}</p>
                <Link
                  to="/prenota"
                  className="mt-6 inline-flex items-center gap-2 text-crema font-semibold text-[15px] md:text-base hover:text-arancio transition-colors duration-300"
                  data-testid={`altra-link-${i}`}
                >
                  {p.link}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComeFunziona() {
  return (
    <section className="bg-crema py-24 lg:py-32" data-testid="come-funziona">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>Come funziona</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink max-w-3xl">
            Dal primo messaggio al referto, in tre passi.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {PASSI.map((s, i) => (
            <Reveal key={s.num} delay={0.1 * i}>
              <div className="border-t-2 border-arancio pt-6" data-testid={`passo-${i}`}>
                <span className="font-sora text-[15px] md:text-base font-semibold tracking-widest text-arancio">{s.num}</span>
                <h3 className="mt-3 font-sora font-bold tracking-tight text-xl text-ink">{s.title}</h3>
                <p className="mt-3 text-viole text-[15px] md:text-base leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrimaDiPrenotare() {
  return (
    <section className="bg-crema-warm py-24 lg:py-32" data-testid="prima-di-prenotare">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>Prima di prenotare</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink max-w-3xl">
            Le domande che ti fai, e che non sempre chiedi.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <Accordion type="single" collapsible className="w-full mt-10">
            {PRE_PNOTA.map((f, i) => (
              <AccordionItem key={i} value={`pre-${i}`} className="border-b border-viole/20" data-testid={`pre-item-${i}`}>
                <AccordionTrigger
                  className="text-left font-sora font-semibold tracking-tight text-lg md:text-xl text-ink py-7 hover:text-indaco hover:no-underline transition-colors duration-300"
                  data-testid={`pre-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[17px] md:text-lg text-viole leading-relaxed pb-7 max-w-2xl">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

function CtaFinale() {
  return (
    <section className="bg-indaco-deep text-crema py-24 lg:py-32" data-testid="prestazioni-cta">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-sora font-bold tracking-tighter text-3xl md:text-5xl">
            Non sai quale esame fa per te?
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-crema/70 text-lg max-w-xl mx-auto">
            Capita a molti, spesso dopo mesi di ipotesi. Scrivici i tuoi sintomi: ti diciamo con sincerità cosa ha senso fare.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/prenota"
              className="group inline-flex items-center gap-3 rounded-full bg-crema px-8 py-4 text-[15px] md:text-base font-semibold text-ink transition-colors duration-300 hover:bg-arancio hover:text-crema"
              data-testid="prestazioni-cta-prenota"
            >
              Richiedi un appuntamento →
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-crema/30 px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:border-crema"
              data-testid="prestazioni-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Chiedi su WhatsApp
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          <p className="mt-6 text-[15px] md:text-base text-crema/60">Ti rispondiamo noi entro 24 ore. Senza impegno.</p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Prestazioni() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const el = document.querySelector(location.hash);
    if (!el) return;
    const t = setTimeout(() => {
      if (lenisRef.current) {
        lenisRef.current.scrollTo(el, { offset: -96 });
      } else {
        el.scrollIntoView({ block: "start" });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <>
      <PageHero />
      {EXAM_BLOCKS.map((e, i) => (
        <ExamBlock key={e.id} exam={e} index={i} />
      ))}
      <AltrePrestazioni />
      <ComeFunziona />
      <PrimaDiPrenotare />
      <CtaFinale />
    </>
  );
}
